import { get } from 'lodash-es';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import { AnchorButton, Button, Modal } from '@payaca/ambrosia';
import CheckboxField from '@payaca/components/checkboxField/CheckboxField';
import FieldLabel from '@payaca/components/fieldLabel/FieldLabel';
import Tooltip from '@payaca/components/tooltip/Tooltip';
import AddBacsModal from '../addBacsModal/AddBacsModal';

import { actions as userActions } from '@/api/users';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import UntitledIcon from '@payaca/untitled-icons';

import { usePermissions } from '@/hooks/usePermissions';

import { useSelector } from '@/api/state';
import './PaymentOptionsControl.sass';

type Props = {
  onChange: any;
  showBACSPaymentOption: boolean;
  showStripePaymentOption: boolean;
};

const PaymentOptionsControl: FC<Props> = ({
  onChange,
  showBACSPaymentOption,
  showStripePaymentOption,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [showAddBacsDetails, setShowAddBacsDetails] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const { userHasRequiredPermission } = usePermissions();

  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const isConnectedToStripe = useMemo(
    () => account && account.connectedToStripe,
    [account]
  );

  const hasAddedBankInformation = useMemo(
    () =>
      account &&
      account.accountNameRaw &&
      account.accountNumberRaw &&
      account.sortCodeRaw,
    [account]
  );

  const interval = useRef<any>(null);

  useEffect(() => {
    return () => clearInterval(interval.current);
  }, []);

  const setCheckStripeStatusInterval = () => {
    clearInterval(interval.current);
    interval.current = setInterval(() => checkStripeStatus(), 3000);
  };

  const checkStripeStatus = () => {
    dispatch(
      userActions.getProfile((err: any, response: any) => {
        const connectedToStripe = get(response, [
          'accounts',
          0,
          'connectedToStripe',
        ]);

        if (connectedToStripe) {
          onChange({
            showStripePaymentOption: true,
          });
          clearInterval(interval.current);
        }
      })
    );
  };

  const checkBacsDetails = useCallback(() => {
    if (hasAddedBankInformation) {
      onChange({
        showBACSPaymentOption: true,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onChange'
  }, [hasAddedBankInformation]);

  const onBankDetailsChange = useCallback(
    (showBACSPaymentOptionValue: boolean) => {
      if (hasAddedBankInformation) {
        onChange({
          showBACSPaymentOption: showBACSPaymentOptionValue,
        });
      } else {
        setShowAddBacsDetails(true);
      }
    },
    [hasAddedBankInformation, onChange]
  );

  const onStripeChange = useCallback(
    (showStripePaymentOptionValue: boolean) => {
      if (showStripePaymentOptionValue && !isConnectedToStripe) {
        setCheckStripeStatusInterval();
        setShowStripeModal(true);
      } else {
        onChange({
          showStripePaymentOption: showStripePaymentOptionValue,
        });
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'setCheckStripeStatusInterval'
    [isConnectedToStripe, onChange]
  );

  return (
    <div className="payment-options-control">
      <FieldLabel label="Payment options" />
      <div className="payment-checkbox-options-wrapper">
        <CheckboxField
          name="stripe"
          label={
            <div className="stripe-label">
              <span>Stripe</span>
              <Tooltip text="Enables your customer to make online card payments or bank transfers directly into your Stripe account, as well as any other payment methods you enable." />
            </div>
          }
          onChange={(value: { [key: string]: any }) =>
            onStripeChange(value.stripe)
          }
          value={showStripePaymentOption}
          isDisabled={
            !isConnectedToStripe &&
            !userHasRequiredPermission([AccountsPermissions.ADD_CONNECTION])
          }
        />

        <CheckboxField
          name="bankDetails"
          label={
            <div className="bacs-label">
              <span>Direct bank transfer</span>
              <Tooltip text="Displays your bank details to enable a client to make a bank transfer." />
            </div>
          }
          onChange={(value: { [key: string]: any }) =>
            onBankDetailsChange(value.bankDetails)
          }
          value={showBACSPaymentOption}
          isDisabled={
            !hasAddedBankInformation &&
            !userHasRequiredPermission([AccountsPermissions.UPDATE_ACCOUNT])
          }
        />
        <AddBacsModal
          isOpen={showAddBacsDetails}
          onClose={() => {
            setShowAddBacsDetails(false);
            checkBacsDetails();
          }}
        />
      </div>
      <Modal
        isOpen={showStripeModal}
        title="Accept card payments with Stripe"
        onClose={() => setShowStripeModal(false)}
        size="sm"
      >
        <Modal.Body>
          <div>
            <div style={{ marginBottom: '10px' }}>
              Stripe is a secure payment platform, trusted by over a million
              businesses all over the world.
            </div>
            <div style={{ marginBottom: '10px' }}>
              If you already have a Stripe account you can connect to it or if
              you don't you can create one.
            </div>
            <div style={{ textAlign: 'left', marginBottom: '10px' }}>
              <div>
                <UntitledIcon name="check" className="size-4" /> Make it easy
                for customers to pay fast with a button on your quotes and
                invoices.
              </div>
              <div>
                <UntitledIcon name="check" className="size-4" /> Fast, easy
                setup
              </div>
              <div>
                <UntitledIcon name="check" className="size-4" /> Fees for
                European cards are 1.4% +20p, you can find out more{' '}
                <a
                  href="https://stripe.com/gb/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={() => setShowStripeModal(false)}
            >
              No thanks
            </Button>
            <AnchorButton
              href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${import.meta.env.VITE_STRIPE_CLIENT_ID}&scope=read_write&redirect_uri=${window.location.origin}/enableCustomerPayments`}
            >
              Connect with Stripe
            </AnchorButton>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentOptionsControl;
